import React, { useReducer, createContext, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { mockApiCalls } from '../../utils/mockData';

export const STATE_NAME = "STATE"

const initialState = {
    // Existing state
    visitors: [],
    guidVisitor: null,
    user: null,
    visitorSubmited: null,

    // New state for enhanced functionality
    isAuthenticated: false,
    loading: false,
    initializing: true, // Add initialization loading state
    announcements: [],
    testimonials: [],
    pricing: [],
    blog: [],
    faqs: [],
    features: [],
    stats: {},
    team: [],
    error: null,
    notifications: [],
    payments: [],
    allPayments: [],
    paymentSubmited: null,
    amenitiesByHome: [],
    amenities: [],
    amenitySubmited: null,
    reservedAmenities: [],
    owners: [],
    ownerSubmited: null,

    // New domains - Accounting
    operations: [],
    periods: [],

    // New domains - Catalogs
    categoryOptions: [],

    // New domains - Fraccionamientos
    fraccs: [],

    // Visitor Invitation Security Flow
    currentInvitation: null,
}

// Safely parse persisted state with error handling
let persistedState = {};
try {
    const storedState = sessionStorage.getItem(STATE_NAME);
    if (storedState) {
        persistedState = JSON.parse(storedState);
    }
} catch (error) {
    console.warn('Failed to parse persisted state:', error);
    sessionStorage.removeItem(STATE_NAME);
}

const finalInitialState = { ...initialState, ...persistedState }

const appReducer = (state, action) => {
    switch (action.type) {
        // Existing actions
        case "SETVISITORS":
            return { ...state, visitors: action.payload }
        case "SETGUIDVISITOR":
            return { ...state, guidVisitor: action.payload }
        case "SETUSER":
            return { ...state, user: action.payload, isAuthenticated: !!action.payload }
        case "SETVISITORSUBMITED":
            return { ...state, visitorSubmited: action.payload }
        case "SETPAYMENTS":
            return { ...state, payments: action.payload }
        case "SET_ALL_PAYMENTS":
            return { ...state, allPayments: action.payload }
        case "SETPAYMENTSUBMITED":
            return { ...state, paymentSubmited: action.payload }
        case "SETHOMEAMENITIES":
            return { ...state, amenitiesByHome: action.payload }
        case "SETAMENITIES":
            return { ...state, amenities: action.payload }
        case "SETAMENITYSUBMITED":
            return { ...state, amenitySubmited: action.payload }
        case "SETRESERVEDAMENITIES":
            return { ...state, reservedAmenities: action.payload }
        case "SET_LOADING":
            return { ...state, loading: action.payload };
        case "SET_INITIALIZING":
            return { ...state, initializing: action.payload };
        case "SET_OWNERS":
            return { ...state, owners: action.payload };
        case "SET_OWNER_SUBMITED":
            return { ...state, ownerSubmited: action.payload };
        case "SET_ANNOUNCEMENTS":
            return { ...state, announcements: action.payload };
        case "SET_TESTIMONIALS":
            return { ...state, testimonials: action.payload };
        case "SET_PRICING":
            return { ...state, pricing: action.payload };
        case "SET_BLOG":
            return { ...state, blog: action.payload };
        case "SET_FAQS":
            return { ...state, faqs: action.payload };
        case "SET_FEATURES":
            return { ...state, features: action.payload };
        case "SET_STATS":
            return { ...state, stats: action.payload };
        case "SET_TEAM":
            return { ...state, team: action.payload };
        case "SET_ERROR":
            return { ...state, error: action.payload };
        case "CLEAR_ERROR":
            return { ...state, error: null };
        case "ADD_NOTIFICATION":
            return {
                ...state,
                notifications: [...state.notifications, action.payload]
            };
        case "REMOVE_NOTIFICATION":
            return {
                ...state,
                notifications: state.notifications.filter(n => n.id !== action.payload)
            };
        case "LOGOUT":
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                guidVisitor: null,
                visitorSubmited: null
            };
        // Accounting domain actions
        case "SET_OPERATIONS":
            return { ...state, operations: action.payload };
        case "SET_PERIODS":
            return { ...state, periods: action.payload };
        // Catalogs domain actions
        case "SET_CATEGORY_OPTIONS":
            return { ...state, categoryOptions: action.payload };
        // Fraccionamientos domain actions
        case "SET_FRACCS":
            return { ...state, fraccs: action.payload };
        // Invitation security flow
        case "SET_CURRENT_INVITATION":
            return { ...state, currentInvitation: action.payload };
        default:
            return state
    }
}

export const AppContext = createContext()

export const AppProvider = props => {
    const [state, dispatch] = useReducer(appReducer, finalInitialState)

    // Helper function to create notifications
    const createNotification = (message, type = 'info', duration = 5000) => {
        const id = Date.now().toString();
        const notification = { id, message, type, timestamp: new Date() };

        dispatch({ type: "ADD_NOTIFICATION", payload: notification });

        if (duration > 0) {
            setTimeout(() => {
                dispatch({ type: "REMOVE_NOTIFICATION", payload: id });
            }, duration);
        }

        return id;
    };

    // Import actions from appActions.js
    // eslint-disable-next-line import/no-relative-packages
    const { createAppActions } = require('./appActions');
    const actions = createAppActions(state, dispatch, createNotification);

    // Initialize app data on mount
    useEffect(() => {
        const initializeApp = async () => {
            try {
                // Check for existing token first
                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        const response = await mockApiCalls.getProfile();
                        if (response.success) {
                            dispatch({ type: "SETUSER", payload: response.data.user });
                        }
                    } catch (error) {
                        localStorage.removeItem('token');
                        console.error('Failed to fetch profile:', error);
                    }
                }

                // Load initial domain data for the new global state implementations
                await Promise.all([
                    actions.fetchOperations(),
                    actions.fetchPeriods(), 
                    actions.fetchCategoryOptions(),
                    actions.fetchFraccs()
                ]);
            } catch (error) {
                console.error('Failed to initialize domain data:', error);
            } finally {
                // Mark initialization as complete
                dispatch({ type: "SET_INITIALIZING", payload: false });
            }

            // Load initial content data
            // await Promise.all([
            //     actions.fetchTestimonials(3),
            //     actions.fetchPricing(),
            //     actions.fetchFaqs(),
            //     actions.fetchFeatures(),
            //     actions.fetchStats()
            // ]);
        };

        initializeApp();
    }, []);

    useEffect(() => {
        // Do not mutate state directly; only persist state
        sessionStorage.setItem(STATE_NAME, JSON.stringify(state))
    }, [state])

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = React.useMemo(() => ({ state, dispatch, actions }), [state, dispatch, actions]);

    return (
        <AppContext.Provider value={contextValue}>
            {props.children}
        </AppContext.Provider>
    )
}

AppProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.arrayOf(PropTypes.element)
    ])
}

// Custom hook to use context with enhanced functionality
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};